import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  useDashboardDataTableState,
  type DashboardDataTableColumn,
} from '@shigo-ui/dashboard-ui';

import { compareNullableValues } from '@shigo-ui/utils';

import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../lib/messages';
import type { ReportExplorerRouteSearch } from '../../../reporting/reportExplorer/reportExplorerRouteSearch';
import type { DashboardWidgetAction } from '../../model';
import { DashboardWidgetActions } from '../DashboardWidgetActions';
import {
  findDashboardWidgetTableScrollport,
  readDashboardWidgetTableBodyMaxHeight,
} from './DashboardWidgetTable.helpers';

interface DashboardWidgetTableProps<TItem> {
  title: string;
  searchPlaceholder: string;
  items: TItem[];
  columns: DashboardDataTableColumn<TItem>[];
  getRowKey: (item: TItem) => string;
  searchText: (item: TItem) => string;
  sortValue: (item: TItem, key: string) => string | number | null | undefined;
  pageSize: number;
  loading: boolean;
  errorMessage?: ReactNode | undefined;
  actions: readonly DashboardWidgetAction[];
  reportingSearch?: ReportExplorerRouteSearch | null | undefined;
  headerControls?: ReactNode | undefined;
  minWidthClassName?: string | undefined;
  searchable?: boolean | undefined;
  footerNote?: ReactNode | undefined;
  paginated?: boolean | undefined;
}

const pageSizes = [5, 10, 25, 50, 100];
const unconstrainedPageSize = 10;
const searchDebounceMs = 650;

export function DashboardWidgetTable<TItem>({
  title,
  searchPlaceholder,
  items,
  columns,
  getRowKey,
  searchText,
  sortValue,
  pageSize,
  loading,
  errorMessage,
  actions,
  reportingSearch,
  headerControls,
  minWidthClassName = 'min-w-[840px]',
  searchable = true,
  footerNote,
  paginated = true,
}: DashboardWidgetTableProps<TItem>) {
  const sortableColumns = useMemo(
    () => columns.map((column) => ({ ...column, sortable: true })),
    [columns],
  );
  const [search, setSearch] = useState('');
  const filtered = items.filter((item) =>
    searchText(item).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  const table = useDashboardDataTableState({
    initialPageSize: pageSize,
    totalItems: filtered.length,
    pageSizes,
    disabled: loading,
  });
  const { page, pageSize: size, sort, resetPage } = table;
  const searchDraft = useDebouncedValue(
    search,
    (next) => {
      setSearch(next);
      resetPage();
    },
    searchDebounceMs,
  );
  const [bodyMaxHeight, setBodyMaxHeight] = useState<number | undefined>();
  const sectionRef = useRef<HTMLElement>(null);
  const previousSizeRef = useRef(size);
  const constrainBody = size > unconstrainedPageSize;
  const sorted = sort
    ? [...filtered].sort((left, right) =>
        compareNullableValues(
          sortValue(left, sort.key),
          sortValue(right, sort.key),
          sort.direction === DashboardDataTableSortDirection.Desc,
        ),
      )
    : filtered;
  const visible = paginated ? sorted.slice(page * size, page * size + size) : sorted.slice(0, size);

  useLayoutEffect(() => {
    const previousSize = previousSizeRef.current;
    previousSizeRef.current = size;
    if (size <= unconstrainedPageSize || size === previousSize) return;
    sectionRef.current?.scrollIntoView({ block: 'start', inline: 'nearest' });
  }, [size]);

  useLayoutEffect(() => {
    if (!constrainBody) return;
    const section = sectionRef.current;
    if (!section) return;

    const update = () => {
      setBodyMaxHeight(readDashboardWidgetTableBodyMaxHeight(section, visible.length));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(section);
    const table = section.querySelector('table');
    if (table) observer.observe(table);
    const scrollport = findDashboardWidgetTableScrollport(section);
    if (scrollport) observer.observe(scrollport);
    return () => {
      observer.disconnect();
    };
  }, [constrainBody, visible.length]);

  return (
    <section ref={sectionRef} className="min-w-0 space-y-3">
      <DashboardDataTable
        className="max-w-none"
        tableLabel={title}
        cardTitle={title}
        cardActions={
          <>
            {searchable ? (
              <DashboardTableSearchInput
                value={searchDraft.draft}
                onChange={(event) => {
                  searchDraft.setDraft(event.target.value);
                }}
                placeholder={searchPlaceholder}
                containerClassName="w-full sm:w-60"
                className="bg-muted h-8 text-xs"
              />
            ) : null}
            {headerControls}
            <DashboardWidgetActions compact actions={actions} reportingSearch={reportingSearch} />
          </>
        }
        columns={sortableColumns}
        sort={table.sortProps}
        items={visible}
        getRowKey={getRowKey}
        loading={loading}
        loadingMessage={dashboardT('dashboard.widget.loading')}
        errorMessage={errorMessage}
        emptyMessage={dashboardT('dashboard.widget.empty')}
        minWidthClassName={minWidthClassName}
        maxBodyHeight={constrainBody ? bodyMaxHeight : undefined}
        pagination={paginated ? table.pagination : undefined}
      />
      {footerNote ? <div className="text-muted-foreground px-4 text-xs">{footerNote}</div> : null}
    </section>
  );
}
